/**
 * DonationSheet — 개발자 후원 · 건전지 (화면정의서 SC-023 · 수익모델 §4).
 *
 * 진입: 내 정보 하단 상설 진열, 그리고 넛지 세 자리 —
 *   스캔 성공 직후 · 컬렉션 저장 완료 · 공유 내보낸 뒤 (FN-059).
 *
 * ── 이 화면이 지켜야 하는 것 ──────────────────────────────────────────
 * 프레임은 **구걸이 아니라 "PEEKA 를 계속 돌린다"** 이다. 그래서:
 *   · 보상은 표식뿐이라고 **먼저** 말한다. 광고가 사라지지 않고 기능 차이도
 *     없다는 것을 감추면, 받은 뒤에 배신이 된다
 *   · 감성 과잉·FOMO 금지 (BRAND §3.7) — '여러분의 도움이 필요합니다',
 *     '지금 아니면' 같은 말을 쓰지 않는다
 *   · 표식 카피는 §3.10 그대로 — '내 건전지 3개'
 *
 * ⚠ 요청 문구를 두 층으로 나눴다 (2026-08-25 결정). §3.10 은 '건전지 하나
 *   넣어주시겠어요?' 한 줄이었는데, 건전지는 **내는 방법**이지 내는 이유가
 *   아니라서 그것만으로는 무엇에 쓰이는지가 안 보였다.
 *     제목  '이 서비스를 함께 키워주시겠어요?'  — 왜 내는가
 *     설명  '…건전지 하나만 보태주세요'          — 어떻게 내는가
 *   은유는 설명·버튼·표식에 남는다.
 *   → BRAND §3.10 표의 '요청' 줄도 함께 갱신해야 문서와 어긋나지 않는다.
 *
 * ── 결제 (2026-09-02 배선) ─────────────────────────────────────────
 *   `native/iap.ts`(cordova-plugin-purchase) → 서버 검증 → 축하.
 *   **성공 판정은 서버 검증까지 끝난 시점**이다 — 스토어 결제만 끝난 상태로 축하를
 *   띄우면, 검증이 실패했을 때 넣지도 않은 건전지를 보여 주게 된다.
 *
 * ⚠ 가격은 **스토어가 준 로케일 가격**을 그린다. 상수(DONATION_PRICE_KRW)는 조회 전·
 *   실패 시의 폴백일 뿐이다 — 그대로 그리면 en/ja 스토어에서 "3,900원" 이라 써 놓고
 *   $2.99 를 청구하는 꼴이 되고, 부정확한 메타데이터로 심사에서 걸린다.
 *
 * ⚠ 데스크탑 웹에는 스토어가 **영구히** 없다. 그때는 결제 버튼 대신 "앱에서 응원할 수
 *   있어요" 를 보여 준다 — 표식은 서버 값이라 웹에서도 그대로 보인다.
 */
import { useEffect, useState } from "react";
import { Button } from "@astryxdesign/core/Button";
import { Heading } from "@astryxdesign/core/Heading";
import { Text } from "@astryxdesign/core/Text";
import { VStack } from "@astryxdesign/core/Layout";
import { useTranslation } from "react-i18next";
import Sheet from "../feedback/Sheet";
import DonationCheer from "./DonationCheer";
import BatteryPictogram from "./BatteryPictogram";
import { useToast } from "../feedback/Toast";
import { initIap, isIapAvailable, purchaseBattery } from "../../native/iap";
import { formatKrw } from "../../lib/formatPrice";
import { DONATION_PRICE_KRW, batteryTier } from "../../data/donation";

interface DonationSheetProps {
  open: boolean;
  onClose: () => void;
  /** 지금까지 넣은 건전지 수 */
  count: number;
  /** 넣기가 끝났을 때 — 부모가 개수를 다시 읽게 한다 */
  onDonated?: () => void;
  /**
   * 회원 식별자. 플러그인이 obfuscator('uuid')로 UUID 화해 스토어에 실어 보낸다
   * (Apple appAccountToken / Google obfuscatedAccountId). 없으면 결제를 시작하지 않는다 —
   * 표식이 계정 기준이라 붙일 곳이 없다.
   */
  memberKey?: string | null;
}

export default function DonationSheet({ open, onClose, count, onDonated, memberKey }: DonationSheetProps) {
  const { t } = useTranslation("me");
  const toast = useToast();
  const tier = batteryTier(count);

  /** 서버 검증까지 끝난 뒤에 뜨는 축하(DonationCheer) */
  const [cheering, setCheering] = useState(false);
  /** 축하 화면에 보여 줄 '넣은 뒤' 개수 */
  const [cheerCount, setCheerCount] = useState(count + 1);
  /** 결제~검증이 도는 동안. 이 사이 버튼을 두 번 누르면 두 번 결제된다 */
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  /** 스토어가 준 로케일 가격. 없으면 폴백 상수 */
  const [price, setPrice] = useState<string | null>(null);
  const iapReady = isIapAvailable();

  /* 스토어 상품 조회는 시트를 열 때 한 번. 실패해도 화면은 폴백 가격으로 뜬다 —
     가격을 못 읽었다고 후원 자체를 막을 이유는 없다(구매 시점에 다시 확인된다). */
  useEffect(() => {
    if (!open || !iapReady || !memberKey) return;
    let alive = true;
    void initIap(memberKey)
      .then((p) => { if (alive) setPrice(p?.localizedPrice ?? null); })
      .catch(() => { /* 폴백 가격으로 계속 */ });
    return () => { alive = false; };
  }, [open, iapReady, memberKey]);

  const handleDonate = async () => {
    if (busy) return;

    /* 결제가 불가능한 환경(운영 데스크탑 웹)에서는 버튼 자체가 안 뜬다.
       비운영 빌드는 stub 결제라 여기 오지 않는다(native/iap isStubIap). */
    if (!iapReady) {
      toast.info(t("donation.pending"));
      onClose();
      return;
    }

    setBusy(true);
    setFailed(false);
    try {
      const result = await purchaseBattery();
      if (result === null) return; // 사용자가 취소 — 아무 일도 없었던 것처럼 둔다
      /* 서버 검증까지 끝났다. 개수는 훅이 서버에서 다시 읽어 오므로, 축하 화면에는
         '넣은 뒤' 값을 낙관적으로 보여 준다(+1). */
      setCheerCount(count + 1);
      setCheering(true);
    } catch {
      /* ⚠ 실패해도 시트를 닫지 않는다. 결제는 됐는데 검증이 안 된 상태일 수 있고,
         그때 스토어 트랜잭션은 finish 되지 않아 재시도로 복구된다. 닫아 버리면
         사용자가 재시도할 자리가 사라진다. */
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  const handleCheerDone = () => {
    setCheering(false);
    onDonated?.();
    onClose();
  };

  if (cheering) {
    /* 축하가 도는 동안에는 시트를 걷는다 — 뒤에 시트가 남아 있으면 축하가
       '시트 위에 뜬 또 하나의 창' 이 된다. 개수는 **넣은 뒤** 값이라 +1 이다. */
    return <DonationCheer count={cheerCount} onDone={handleCheerDone} />;
  }

  return (
    <Sheet open={open} onClose={onClose} ariaLabel={t("donation.title")}>
      <VStack gap={4} padding={5}>
        <VStack gap={2} align="start">
          <Heading level={3}>{t("donation.title")}</Heading>
          {/* 무엇에 쓰이는 돈인지 한 줄로. 기능을 사는 게 아니라는 걸 여기서 말한다.
              ⚠ **아직 한 번도 안 넣은 사람에게만** 보인다 (2026-08-27). 이미 넣어
                준 사람에게 같은 권유를 다시 읽히면, 아래 '내 건전지 N개' 와 나란히
                서서 '더 내라' 로 읽힌다 — 후원은 조르지 않는다(BRAND §3.7). */}
          {tier === "none" && (
            <Text type="supporting" color="secondary">{t("donation.body")}</Text>
          )}
        </VStack>

        {/* 지금 내 건전지 — 0개면 그리지 않는다.
            '건전지 0개' 는 부재를 세는 표시라 만들지 않기로 했다 (수익모델 §4.3).
            ⚠ 상자를 걷었다 (2026-08-27). Card 로 감싸면 '고를 것' 처럼 보이는데
              이건 **지금 상태를 알려 주는 한 줄**이다 — 누를 것이 아니다. */}
        {tier !== "none" && (
          <VStack gap={2} align="center">
            {/* 여기까지 온 것을 그림이 말한다 (2026-08-27). 개수는 아래 줄이
                이미 세고 있는데, 숫자만으로는 '3개' 와 '7개' 가 같은 무게로
                읽힌다. 차오른 그림은 그 사이를 보여 준다. */}
            <BatteryPictogram count={count} size="sheet" />
            <Text type="supporting" color="secondary">
              {t("donation.mine", { count })}
            </Text>
          </VStack>
        )}

        <VStack gap={2}>
          {/* 데스크탑 웹에는 스토어가 영구히 없다 — 결제 버튼 대신 사실을 말한다.
              표식은 서버 값이라 이 화면에서도 그대로 보인다. */}
          {!iapReady ? (
            <Text type="supporting" color="secondary">{t("donation.appOnly")}</Text>
          ) : (
            <Button
              variant="primary"
              size="lg"
              label={
                busy
                  ? t("donation.checking")
                  : failed
                    ? t("donation.retry")
                    : t("donation.cta", { price: price ?? formatKrw(DONATION_PRICE_KRW) })
              }
              isDisabled={busy}
              clickAction={() => void handleDonate()}
              width="100%"
            />
          )}
          {/* ⚠ 실패해도 시트를 닫지 않는다 — 결제는 됐는데 검증이 안 된 상태일 수 있고,
              스토어 트랜잭션이 finish 되지 않아 재시도로 복구된다. */}
          {failed && (
            <Text type="supporting" color="secondary">{t("donation.failed")}</Text>
          )}
          {/* 고지 — 받기 전에 말한다. 이 한 줄이 후원과 결제를 가른다.
              ⚠ 명세(SC-023)는 여기에 '광고는 계속 노출됩니다' 도 함께 적으라고
                한다. 광고가 아직 앱에 없어서 지금 적으면 없는 것을 설명하게 된다 —
                광고가 붙는 시점에 이 문구에 더한다. */}
          <Text type="supporting" color="secondary">{t("donation.notice")}</Text>
        </VStack>
      </VStack>
    </Sheet>
  );
}
